import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Globe2, KeyRound, Lock, MessageCircle, RefreshCw, Sparkles, Tag, UserPlus, X } from "lucide-react";
import { checkSpaceNameAvailability, createSpace, type Space } from "../../api/spaces";
import {
    clearCreateSpaceDraft,
    MIN_ACCESS_CODE_LENGTH,
    readCreateSpaceDraft,
    validateCreateSpace,
    writeCreateSpaceDraft,
    type CreateSpaceAccessMode,
} from "../../pages/createSpaceDraft";
import { focusableElements } from "../../utils/accessibility";
import { SPACE_ACCENTS } from "./spaceCatalog";

function cleanAccentColor(value?: string) {
    return /^#[0-9a-fA-F]{6}$/.test(value || "") ? String(value).toLowerCase() : "#22d3ee";
}

function alphaHex(hex: string, alpha: string) {
    return `${cleanAccentColor(hex)}${alpha}`;
}

function accessLanguage(mode: string) {
    if (mode === "private") {
        return {
            codeLabel: "Invite code",
        };
    }
    return {
        codeLabel: "Lock code",
    };
}

function generateAccessCode() {
    const letters = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const digits = "23456789";
    const alphabet = `${letters}${digits}`;
    const bytes = new Uint8Array(8);
    if (window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(bytes);
    } else {
        for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 255);
    }
    const body = Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join("");
    return `${letters[bytes[0] % letters.length]}${digits[bytes[1] % digits.length]}${body}`.slice(0, 10);
}

type CreateSpaceModalProps = {
    onClose: () => void;
    onCreated: (space: Space) => void;
};

export default function CreateSpaceModal({ onClose, onCreated }: CreateSpaceModalProps) {
    const initialDraft = useMemo(() => readCreateSpaceDraft(), []);
    const [title, setTitle] = useState(initialDraft?.title || "");
    const [desc, setDesc] = useState(initialDraft?.desc || "");
    const [accentColor, setAccentColor] = useState(cleanAccentColor(initialDraft?.accentColor || SPACE_ACCENTS[0]));
    const [accessMode, setAccessMode] = useState<CreateSpaceAccessMode>(["open", "locked", "private"].includes(initialDraft?.accessMode || "") ? initialDraft?.accessMode as CreateSpaceAccessMode : "open");
    const [inviteCode, setInviteCode] = useState("");
    const [inviteExpiresAt, setInviteExpiresAt] = useState(initialDraft?.inviteExpiresAt || "");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [nameError, setNameError] = useState("");
    const [nameChecking, setNameChecking] = useState(false);
    const [nameAvailable, setNameAvailable] = useState(false);
    const [draftRestored] = useState(Boolean(initialDraft));
    const requestRef = useRef<AbortController | null>(null);
    const nameCheckRef = useRef<AbortController | null>(null);
    const dialogRef = useRef<HTMLFormElement | null>(null);
    const titleInputRef = useRef<HTMLInputElement | null>(null);
    const descTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const restoreFocusRef = useRef<HTMLElement | null>(null);
    const dialogTitleId = useId();
    const nameStatusId = useId();

    function applyAccentColor(value: string) {
        setAccentColor(cleanAccentColor(value));
    }

    const closeModal = useCallback(() => {
        if (!submitting) onClose();
    }, [onClose, submitting]);

    const cancelSubmission = useCallback(() => {
        if (!submitting) {
            closeModal();
            return;
        }
        setError("Cancelling creation. Your draft is saved.");
        requestRef.current?.abort();
    }, [closeModal, submitting]);

    useEffect(() => {
        restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const timer = window.setTimeout(() => titleInputRef.current?.focus(), 0);
        return () => {
            window.clearTimeout(timer);
            document.body.style.overflow = previousOverflow;
            restoreFocusRef.current?.focus();
        };
    }, []);

    useEffect(() => {
        function handleDialogKeyDown(event: KeyboardEvent) {
            if (event.key === "Escape") {
                if (!submitting) {
                    event.preventDefault();
                    closeModal();
                }
                return;
            }
            if (event.key !== "Tab") return;
            const targets = focusableElements(dialogRef.current);
            if (!targets.length) return;
            const first = targets[0];
            const last = targets[targets.length - 1];
            if (!dialogRef.current?.contains(document.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
        document.addEventListener("keydown", handleDialogKeyDown);
        return () => document.removeEventListener("keydown", handleDialogKeyDown);
    }, [closeModal, submitting]);

    useEffect(() => {
        writeCreateSpaceDraft({ title, desc, accentColor, tags: [], tagDraft: "", accessMode, lockedReason: "", inviteExpiresAt });
    }, [accessMode, accentColor, desc, inviteExpiresAt, title]);

    useEffect(() => () => {
        requestRef.current?.abort();
        nameCheckRef.current?.abort();
    }, []);

    useEffect(() => {
        nameCheckRef.current?.abort();
        const candidate = title.trim();
        setNameAvailable(false);

        if (!candidate) {
            setNameChecking(false);
            setNameError("");
            return;
        }

        const controller = new AbortController();
        nameCheckRef.current = controller;
        setNameChecking(true);
        setNameError("");

        const timer = window.setTimeout(async () => {
            try {
                const result = await checkSpaceNameAvailability(candidate, { signal: controller.signal });
                if (controller.signal.aborted) return;
                setNameChecking(false);
                setNameAvailable(result.available);
                setNameError(result.available ? "" : result.message || "A Space with this name already exists.");
            } catch (err: any) {
                if (err?.name === "AbortError" || controller.signal.aborted) return;
                setNameChecking(false);
                setNameAvailable(false);
            }
        }, 320);

        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [title]);

    useEffect(() => {
        const textarea = descTextareaRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 168)}px`;
    }, [desc]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (submitting) return;

        const validationError = validateCreateSpace({
            title, desc, accentColor, tags: [], tagDraft: "", accessMode, inviteCode,
            lockedReason: "", inviteExpiresAt,
        });
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        setError("");
        setNameError("");
        const controller = new AbortController();
        requestRef.current = controller;
        try {
            const nameCheck = await checkSpaceNameAvailability(title.trim(), { signal: controller.signal });
            if (!nameCheck.available) {
                setNameError(nameCheck.message || "A Space with this name already exists.");
                setSubmitting(false);
                requestRef.current = null;
                return;
            }
            const created = await createSpace({
                title: title.trim(),
                desc: desc.trim(),
                accentColor,
                isPublic: accessMode === "open",
                accessMode,
                inviteCode: inviteCode.trim(),
                inviteExpiresAt: inviteExpiresAt ? new Date(inviteExpiresAt).toISOString() : null,
            }, { signal: controller.signal });
            clearCreateSpaceDraft();
            onCreated(created);
            onClose();
        } catch (err: any) {
            if (err?.name !== "AbortError") {
                const message = err?.message || "Could not create space. Your draft is still saved.";
                if (/space name|space with this name|space title/i.test(message)) {
                    setNameError(message);
                } else {
                    setError(message);
                }
            }
        } finally {
            requestRef.current = null;
            setSubmitting(false);
        }
    }

    return (
        <div className="bs-create-space-overlay fixed inset-0 z-[2200] flex items-stretch justify-center overflow-hidden bg-black/70 p-2 backdrop-blur-md sm:items-center sm:p-4" onClick={closeModal}>
            <form
                ref={dialogRef}
                onSubmit={handleSubmit}
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby={dialogTitleId}
                aria-busy={submitting}
                className="bs-create-space-form flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 p-5 shadow-2xl shadow-black/70"
                style={{
                    "--bs-create-space-accent": accentColor,
                    "--bs-create-space-accent-soft": alphaHex(accentColor, "34"),
                    "--bs-create-space-accent-border": alphaHex(accentColor, "aa"),
                } as React.CSSProperties}
            >
                <div className="bs-create-space-header mb-4 flex shrink-0 items-center justify-between">
                    <div>
                        <div className="bs-create-space-title-row">
                            <Sparkles size={16} aria-hidden="true" />
                            <h2 id={dialogTitleId} className="text-xl font-black text-white">Create a Space</h2>
                        </div>
                    </div>
                    <button type="button" onClick={closeModal} disabled={submitting} className="bs-create-space-close inline-flex h-9 w-9 items-center justify-center text-neutral-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40" aria-label="Close Create Space">
                        <X size={16} />
                    </button>
                </div>

                <div className="bs-create-space-body hidden-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
                <fieldset disabled={submitting} className="space-y-4 disabled:opacity-70">
                    {draftRestored && (
                        <p className="rounded-md border border-cyan-900/60 bg-cyan-950/25 px-3 py-2 text-xs text-cyan-200">
                            Your previous draft was restored. Custom access codes are never saved in the browser.
                        </p>
                    )}
                    <div className="bs-create-space-name-row grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 sm:gap-3">
                        <label className="block">
                            <span className="bs-create-space-label mb-1 block text-sm text-neutral-300">Name</span>
                            <span className={`bs-create-space-input-shell ${nameError ? "is-invalid" : nameAvailable && title.trim() ? "is-ok" : ""}`}>
                                <Tag size={16} aria-hidden="true" />
                                <input
                                    ref={titleInputRef}
                                    autoComplete="off"
                                    required
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value.slice(0, 100))}
                                    className="bs-create-space-control w-full rounded-xl border border-white/10 bg-black/45 px-3 py-2.5 text-white outline-none focus:border-cyan-700"
                                    placeholder="e.g. Privacy Lab"
                                    aria-invalid={Boolean(nameError)}
                                    aria-describedby={nameStatusId}
                                />
                            </span>
                            <span
                                id={nameStatusId}
                                className={`bs-create-space-name-status ${nameError ? "is-error" : nameAvailable && title.trim() ? "is-ok" : ""}`}
                                aria-live="polite"
                            >
                                {nameChecking ? "Checking name..." : nameError || (nameAvailable && title.trim() ? "Space name is available." : "")}
                            </span>
                        </label>
                        <div className="bs-create-space-color-wrap relative self-start">
                            <span className="bs-create-space-label mb-1 block text-sm text-neutral-300">Colour</span>
                            <label
                                aria-label={`Accent color: ${accentColor.toUpperCase()}`}
                                className="bs-create-space-color-button grid h-[46px] w-[46px] place-items-center rounded-xl border border-white/15 outline-none transition hover:border-white/30 focus:border-cyan-500"
                            >
                                <input
                                    type="color"
                                    value={accentColor}
                                    onInput={(event) => applyAccentColor(event.currentTarget.value)}
                                    onChange={(event) => applyAccentColor(event.currentTarget.value)}
                                    onBlur={(event) => applyAccentColor(event.currentTarget.value)}
                                    className="bs-create-space-color-input"
                                    aria-label="Choose space colour"
                                    disabled={submitting}
                                />
                            </label>
                        </div>
                    </div>
                    <label className="block">
                        <span className="bs-create-space-label mb-1 block text-sm text-neutral-300">Description</span>
                        <span className="bs-create-space-textarea-shell">
                            <MessageCircle size={15} aria-hidden="true" />
                            <textarea
                                ref={descTextareaRef}
                                value={desc}
                                onChange={(e) => setDesc(e.target.value.slice(0, 500))}
                                rows={1}
                                className="bs-create-space-control w-full rounded-xl border border-white/10 bg-black/45 px-3 py-2.5 text-white outline-none focus:border-cyan-700"
                                placeholder="What should people talk about here?"
                            />
                            <span className="bs-create-space-counter">{desc.length}/500</span>
                        </span>
                    </label>
                    <div className="bs-create-space-access-panel">
                        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                            <Lock size={15} className="text-cyan-300" />
                            Space access
                        </div>
                        <div className="bs-create-space-access-grid grid grid-cols-3 gap-2" role="group" aria-label="Space access mode">
                            {[
                                { value: "open", label: "Open", description: "Anyone can discover and join", icon: Globe2 },
                                { value: "locked", label: "Locked", description: "Anyone can join with approval", icon: Lock },
                                { value: "private", label: "Invite Only", description: "Only invited people can join", icon: UserPlus },
                            ].map((item) => {
                                const AccessIcon = item.icon;
                                return (
                                    <button
                                        key={item.value}
                                        type="button"
                                        onClick={() => setAccessMode(item.value as "open" | "locked" | "private")}
                                        aria-pressed={accessMode === item.value}
                                        className={`bs-create-space-access-card rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                                            accessMode === item.value
                                                ? "is-selected border-cyan-500 text-cyan-100"
                                                : "border-neutral-800 text-neutral-400 hover:border-neutral-600"
                                        }`}
                                        style={{ background: "transparent" }}
                                    >
                                        <span className="bs-create-space-access-top">
                                            <AccessIcon size={16} aria-hidden="true" />
                                            <span className="bs-create-space-access-radio" />
                                        </span>
                                        <span className="bs-create-space-access-label">{item.label}</span>
                                        <span className="bs-create-space-access-copy">{item.description}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {accessMode !== "open" && (
                            <div className="bs-create-space-locked-panel">
                                <label className="bs-create-space-access-field">
                                    <span className="bs-create-space-field-label">{accessLanguage(accessMode).codeLabel}</span>
                                    <span className="bs-create-space-access-code-row">
                                        <span className="bs-create-space-access-input-shell">
                                            <KeyRound size={14} aria-hidden="true" />
                                            <input
                                                value={inviteCode}
                                                onChange={(e) => setInviteCode(e.target.value.toUpperCase().slice(0, 40))}
                                                className="bs-create-space-access-input"
                                                placeholder="Auto-generated if blank"
                                            />
                                        </span>
                                        <button
                                            type="button"
                                            className="bs-create-space-code-refresh"
                                            onClick={() => setInviteCode(generateAccessCode())}
                                            title={`Generate ${accessLanguage(accessMode).codeLabel.toLowerCase()}`}
                                            aria-label={`Generate ${accessLanguage(accessMode).codeLabel.toLowerCase()}`}
                                        >
                                            <RefreshCw size={14} aria-hidden="true" />
                                        </button>
                                    </span>
                                    <span className="bs-create-space-field-help">{MIN_ACCESS_CODE_LENGTH}+ chars with a letter and number.</span>
                                </label>
                                <label className="bs-create-space-access-field">
                                    <span className="bs-create-space-field-label">Code expiry</span>
                                    <input
                                        type="datetime-local"
                                        value={inviteExpiresAt}
                                        min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                                        onChange={(event) => setInviteExpiresAt(event.target.value)}
                                        className="bs-create-space-access-input"
                                    />
                                    <span className="bs-create-space-field-help">Optional. New entries stop when this time is reached.</span>
                                </label>
                            </div>
                        )}
                    </div>
                </fieldset>

                <div aria-live="assertive" aria-atomic="true">
                    {error && <p role="alert" className="mt-4 rounded-md bg-red-950/40 p-2 text-sm text-red-300">{error}</p>}
                </div>
                </div>

                <div className="bs-create-space-actions mt-4 flex justify-end gap-2 rounded-xl border border-cyan-900/35 bg-cyan-950/10 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                    <button
                        type="button"
                        onClick={cancelSubmission}
                        className="h-10 rounded-md px-4 text-sm font-black text-neutral-400 transition hover:bg-white/[0.06] hover:text-white"
                    >
                        {submitting ? "Cancel request" : "Cancel"}
                    </button>
                    <button
                        type="submit"
                        disabled={submitting || nameChecking || Boolean(nameError)}
                        className="bs-create-space-submit h-10 rounded-md border px-5 text-sm font-black text-black shadow-[0_0_24px_rgba(34,211,238,0.10)] transition disabled:opacity-60"
                    >
                        {submitting ? "Creating..." : "Create Space"}
                    </button>
                </div>
            </form>
        </div>
    );
}
